import Channel, {
    ChannelDestination,
    RequestInfo,
} from 'communication/Channel';
import { addItemToArrayWithMaxSize } from 'utils/arrays/arrayUtils';
import IndigoMessage from 'communication/IndigoMessage';
import StreamState from 'communication/StreamState';

interface DestinationRetentionPolicy {
    maxRequests: number;
    responsesForLatestRequest: number;
    responsesForOlderRequests: number;
}

const DefaultOldConnectionsRetention = 5;

const defaultRetentionPolicy: DestinationRetentionPolicy = {
    maxRequests: 10,
    responsesForLatestRequest: 100,
    responsesForOlderRequests: 10,
};

/*
 * Manages retention of messages in state for a channel.
 * Note: this is a class to future proof for user setting of retention policies per channel
 */
export class ChannelMessageStorageManager {
    private retentionPolicies: Partial<
        Record<ChannelDestination, DestinationRetentionPolicy>
    > = {};
    private oldConnectionsRetention = DefaultOldConnectionsRetention;

    public recordRequest(
        channel: Channel<unknown, unknown>,
        requestInfo: RequestInfo<unknown, unknown>
    ) {
        const policy =
            this.retentionPolicies[channel.destination] ??
            defaultRetentionPolicy;
        const { maxRequests } = policy;

        channel.latestRequests = addItemToArrayWithMaxSize(
            requestInfo,
            channel.latestRequests,
            maxRequests
        );
    }

    public recordResponse(
        channel: Channel<unknown, unknown>,
        message: IndigoMessage<unknown>
    ) {
        const policy =
            this.retentionPolicies[channel.destination] ??
            defaultRetentionPolicy;
        const { responsesForLatestRequest, responsesForOlderRequests } = policy;

        const latestRequest = channel.latestRequests[0];

        const receivedTime = new Date();
        if (!latestRequest.firstReceivedTime) {
            latestRequest.firstReceivedTime = receivedTime;
        }
        latestRequest.lastReceivedTime = receivedTime;

        latestRequest.latestResponses = addItemToArrayWithMaxSize(
            {
                receivedTime,
                payload: message.payload,
                messageNumber: message.messageNumber,
            },
            latestRequest.latestResponses,
            responsesForLatestRequest
        );
        latestRequest.receivedCount = latestRequest.receivedCount + 1;

        for (let i = 1; i < channel.latestRequests.length; i++) {
            const req = channel.latestRequests[i];
            purgeRequestResponses(req, responsesForOlderRequests);
        }
    }

    public purgeOldConnections(streamState: StreamState) {
        const retention = this.oldConnectionsRetention;
        if (streamState.disposedConnections.length > retention) {
            streamState.disposedConnections =
                streamState.disposedConnections.splice(
                    streamState.disposedConnections.length - retention
                );
        }
    }
}

export function purgeRequestResponses(
    request: RequestInfo<unknown, unknown>,
    retention: number
) {
    if (request.latestResponses.length > retention) {
        request.latestResponses = request.latestResponses.slice(0, retention);
    }
}
